class PromoPopup {
  constructor(container) {
    this.container = container;
    this.popupModal = container.querySelector(".sign-up-popup-modal");
    this.overlay = container.querySelector(".sign-up-popup-overlay");
    this.closeButton = this.popupModal.querySelector(".popup-modal__close");
    this.dismissButton = this.popupModal.querySelector(
      ".popup-modal__dismiss-btn"
    );

    this.form = this.popupModal.querySelector(".newsletter-form");
    this.emailInput = this.form?.querySelector('input[type="email"]');
    this.submitButton = this.form?.querySelector('button[type="submit"]');
    this.formError = this.form?.querySelector(".newsletter-form__message");

    this.successModal = container.querySelector(".success-popup-modal");
    this.successOverlay = container.querySelector(".success-popup-overlay");
    this.copyButton = this.successModal?.querySelector(
      ".popup-modal__copy-btn"
    );
    this.copySuccessMsg = this.successModal?.querySelector(
      ".popup-modal__success-msg"
    );
    this.successCloseButton = this.successModal?.querySelector(
      ".popup-modal__close"
    );
    this.successDismissButton = this.successModal?.querySelector(
      ".popup-modal__dismiss-btn"
    );

    this.sectionId = this.popupModal.dataset.sectionId;
    this.testMode = this.popupModal.dataset.testMode === "true";
    this.delaySeconds = parseInt(this.popupModal.dataset.delaySeconds);
    this.delayDays = parseInt(this.popupModal.dataset.delayDays);
    this.displayTimer = this.popupModal.dataset.displayTimer === "true";
    this.timerDuration = parseFloat(this.popupModal.dataset.timerDuration) * 60;

    this.minutesElement = this.popupModal.querySelector(
      ".popup-modal__timer__minutes"
    );
    this.secondsElement = this.popupModal.querySelector(
      ".popup-modal__timer__seconds"
    );

    // Versioned key allows the corrected offer to show despite old popup dismissal records.
    this.storageKey = `promo-popup-v2-${window.location.host}-${this.sectionId}`;
    this.previouslyFocusedElement = null;

    this.init();
  }

  init() {
    this.setupEventListeners();

    if (this.popupModal.classList.contains("popup-modal--active")) {
      this.popupModal.setAttribute("aria-hidden", "false");
    }
    if (this.successModal?.classList.contains("popup-modal--active")) {
      this.successModal.setAttribute("aria-hidden", "false");
    }

    // Shopify has processed the native customer form on the server.
    // Never display the discount as a successful signup until this flag exists.
    if (this.container.querySelector('[data-dd-signup-confirmed]')) {
      try { localStorage.setItem('dd-newdork10-subscribed', 'yes'); } catch (error) {}
      this.showSuccessPopup();
    } else if (!this.testMode) {
      this.checkAndShowPopup();
    }
    if (this.form) this.setupFormSubmission();

    if (this.testMode) {
      if (this.displayTimer && this.popupModal.classList.contains("popup-modal--active")) this.startTimer();
      return;
    }
  }

  setupEventListeners() {
    this.closeButton.addEventListener("click", () => this.closePopup());
    if (this.dismissButton) {
      this.dismissButton.addEventListener("click", () => this.closePopup());
    }
    this.overlay.addEventListener("click", () => this.closePopup());
    document.addEventListener("keydown", (event) => this.handleKeydown(event));

    if (this.successModal) {
      if (this.copyButton) {
        this.copyButton.addEventListener("click", () => this.handleCopyCode());
      }

      if (this.successCloseButton) {
        this.successCloseButton.addEventListener("click", () =>
          this.closeSuccessPopup()
        );
      }
      if (this.successDismissButton) {
        this.successDismissButton.addEventListener("click", () =>
          this.closeSuccessPopup()
        );
      }
      if (this.successOverlay) {
        this.successOverlay.addEventListener("click", () =>
          this.closeSuccessPopup()
        );
      }
    }
  }

  getActiveModal() {
    if (this.successModal?.classList.contains("popup-modal--active")) return this.successModal;
    if (this.popupModal?.classList.contains("popup-modal--active")) return this.popupModal;
    return null;
  }

  focusModal(modal) {
    if (!modal) return;
    const focusable = modal.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    const first = focusable[0] || modal;
    if (first === modal && !first.hasAttribute("tabindex")) first.setAttribute("tabindex", "-1");
    window.setTimeout(() => first.focus({ preventScroll: true }), 0);
  }

  restoreFocus() {
    const target = this.previouslyFocusedElement;
    this.previouslyFocusedElement = null;
    if (target && target.isConnected && typeof target.focus === "function") {
      target.focus({ preventScroll: true });
    }
  }

  handleKeydown(event) {
    const modal = this.getActiveModal();
    if (!modal) return;
    if (event.key === "Escape") {
      event.preventDefault();
      if (modal === this.successModal) this.closeSuccessPopup();
      else this.closePopup();
      return;
    }
    if (event.key !== "Tab") return;
    const focusable = Array.from(modal.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )).filter((el) => el.getClientRects().length > 0);
    if (!focusable.length) {
      event.preventDefault();
      modal.focus();
      return;
    }
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault(); first.focus();
    }
  }

  setupFormSubmission() {
    // Shopify's standard customer form must submit normally. An AJAX 200 can be
    // a rendered error page, and does not prove a newsletter subscription.
    this.form.addEventListener('submit', () => {
      if (this.emailInput && !this.emailInput.checkValidity()) return;
      if (this.submitButton) this.submitButton.disabled = true;
    });
  }

  calculateNextDisplayDate() {
    const now = new Date();
    return new Date(
      now.getTime() + this.delayDays * 24 * 60 * 60 * 1000
    ).toISOString();
  }

  checkAndShowPopup() {
    if (this.testMode) return;
    try {
      if (localStorage.getItem('dd-newdork10-subscribed') === 'yes') return;
      const stored = localStorage.getItem(this.storageKey);
      if (stored) {
        const nextDate = new Date(JSON.parse(stored).next_display_date);
        if (nextDate > new Date()) return;
      }
    } catch (error) { /* Storage blocked: still show the offer. */ }
    // A missing or unanswered Shopify privacy API must not deadlock this popup.
    window.setTimeout(() => {
      if (!this.getActiveModal()) this.showPopup();
    }, Math.max(2, Number(this.delaySeconds) || 3) * 1000);
  }

  showPopup() {
    this.previouslyFocusedElement = document.activeElement;
    this.popupModal.classList.add("popup-modal--active");
    this.popupModal.setAttribute("aria-hidden", "false");
    this.overlay.classList.add("popup-overlay--active");
    this.focusModal(this.popupModal);

    if (this.displayTimer) {
      this.startTimer();
    }
  }

  closePopup(restoreFocus = true) {
    this.popupModal.classList.remove("popup-modal--active");
    this.popupModal.setAttribute("aria-hidden", "true");
    this.overlay.classList.remove("popup-overlay--active");

    if (!this.testMode) {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify({
          next_display_date: this.calculateNextDisplayDate(), dismissed: true
        }));
      } catch (error) {}
    }
    if (restoreFocus) this.restoreFocus();
  }

  showSuccessPopup() {
    if (this.successModal) {
      this.closePopup(false);
      this.successModal.classList.add("popup-modal--active");
      this.successModal.setAttribute("aria-hidden", "false");
      this.successOverlay.classList.add("popup-overlay--active");
      this.focusModal(this.successModal);
    }
  }

  closeSuccessPopup() {
    if (this.successModal) {
      this.successModal.classList.remove("popup-modal--active");
      this.successModal.setAttribute("aria-hidden", "true");
      this.successOverlay.classList.remove("popup-overlay--active");
      this.restoreFocus();
    }
  }

  async handleCopyCode() {
    if (this.copyButton) {
      const discountCode = this.successModal.querySelector(
        "#DiscountCode--" + this.sectionId
      );

      if (discountCode) {
        document.body.focus();
        this.copyButton.focus();

        await this.copyToClipboardFallback(discountCode.value);

        if (this.copySuccessMsg) {
          this.copySuccessMsg.style.display = "block";
          setTimeout(() => {
            this.copySuccessMsg.style.display = "none";
          }, 3000);
        }
      } else {
        console.error("Discount code element not found.");
      }
    }
  }

  async copyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error("Failed to copy text using navigator.clipboard:", err);
    }
  }

  copyToClipboardFallback(text) {
    const tempNode = document.createElement("div");
    tempNode.innerHTML = text;

    function listener(e) {
      e.clipboardData.setData("text/html", text);
      e.clipboardData.setData("text/plain", text);
      e.preventDefault();
    }
    document.addEventListener("copy", listener);
    document.execCommand("copy");
    document.removeEventListener("copy", listener);
  }
  showFormError(message) {
    if (this.formError) {
      this.formError.textContent = message;
      this.formError.style.display = "block";

      setTimeout(() => {
        this.formError.style.display = "none";
      }, 5000);
    }
  }

  startTimer() {
    let timeLeft = this.timerDuration;

    const updateTimer = () => {
      const minutes = Math.floor(timeLeft / 60);
      const seconds = timeLeft % 60;

      this.minutesElement.textContent = minutes.toString().padStart(2, "0");
      this.secondsElement.textContent = seconds.toString().padStart(2, "0");

      if (timeLeft > 0) {
        timeLeft--;
        setTimeout(updateTimer, 1000);
      } else {
        this.closePopup();
      }
    };

    updateTimer();
  }
}

document.addEventListener("DOMContentLoaded", function () {
  const sections = document.querySelectorAll(
    '[data-section-type="newsletter-popup"]'
  );
  sections.forEach((section) => {
    new PromoPopup(section.closest("section, .shopify-section"));
  });
});
